import { useCallback, useEffect, useState } from 'react';
import { Upload, Plus, Loader, Lock, FolderOpen } from 'lucide-react';
import { apiGescomExport, apiPersons } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import DatePicker from '../components/DatePicker.jsx';
import GescomSessionDetail from '../components/gescom/GescomSessionDetail.jsx';
import {
  card, thStyle, tdStyle, primaryBtn, secondaryBtn, pill, inputStyle, fmtDay, fmtDateTime, SPIN_CSS,
} from '../components/gescom/gescomUi.jsx';

/**
 * Vue admin « Export Gescom » organisée en SESSIONS.
 *
 * Une session (« Facturation Septembre 2026 ») fige une période et regroupe
 * tous les jobs qui la concernent : un collaborateur, puis une équipe (tag),
 * puis la relance des erreurs… Son suivi recalcule l'état de chaque ligne
 * (poussée / à pousser / en erreur / bloquée) et son journal garde la trace
 * de ce qui a été fait, par qui, avec les erreurs Gescom.
 *
 * La session ouverte est mémorisée (préférence de poste) pour y revenir.
 */

const SESSION_KEY = 'synolog.gescomExport.sessionId';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Période d'un mois relatif (0 = mois en cours, -1 = mois dernier) + nom par défaut. */
function monthPreset(offset, today = new Date()) {
  const first = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  return {
    from: isoLocal(first),
    to: isoLocal(last),
    name: `Facturation ${MONTHS[first.getMonth()]} ${first.getFullYear()}`,
  };
}

function readStoredSession() {
  try { return window.localStorage.getItem(SESSION_KEY); } catch { return null; }
}
function storeSession(id) {
  try {
    if (id) window.localStorage.setItem(SESSION_KEY, id);
    else window.localStorage.removeItem(SESSION_KEY);
  } catch { /* préférence de confort uniquement */ }
}

export default function GescomExportView({ user }) {
  const isAdmin = user?.role === 'ADMIN';
  const [sessionId, setSessionId] = useState(() => readStoredSession());
  const [sessions, setSessions] = useState(null);
  const [listError, setListError] = useState(null);
  const [persons, setPersons] = useState([]);
  const [creating, setCreating] = useState(false);

  const loadSessions = useCallback(async () => {
    try {
      const r = await apiGescomExport.sessions();
      setSessions(r.items ?? []);
      setListError(null);
    } catch (e) {
      setListError(e.message || 'Liste des sessions indisponible');
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void loadSessions();
    apiPersons.list('active').then((r) => setPersons(r?.items ?? r ?? [])).catch(() => {});
  }, [isAdmin, loadSessions]);

  const open = (id) => { setSessionId(id); storeSession(id); };
  const back = () => { setSessionId(null); storeSession(null); void loadSessions(); };

  if (!isAdmin) {
    return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Accès réservé aux administrateurs.</div>;
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Upload size={20} /> Export Gescom
        </h1>
        {!sessionId ? (
          <button type="button" style={{ ...primaryBtn, marginLeft: 'auto' }} onClick={() => setCreating(true)}>
            <Plus size={14} /> Nouvelle session
          </button>
        ) : null}
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>
        Pousse les saisies horaires Synolog vers Gescom (Penta), ou les en retire, au sein d’une session qui fige la période et garde la trace des envois.
      </p>

      {sessionId ? (
        <GescomSessionDetail
          key={sessionId}
          sessionId={sessionId}
          persons={persons}
          onBack={back}
          onChanged={loadSessions}
        />
      ) : (
        <div style={card}>
          {listError ? <p style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}>{listError}</p> : null}
          {sessions === null && !listError ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}><Loader size={13} className="spin" /> Chargement…</p>
          ) : null}
          {sessions && sessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ fontSize: 13.5, color: 'var(--text)' }}>Aucune session d’export pour l’instant.</p>
              <button type="button" style={primaryBtn} onClick={() => setCreating(true)}>
                <Plus size={14} /> Créer la première session
              </button>
            </div>
          ) : null}
          {sessions && sessions.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                    <th style={thStyle}>Session</th>
                    <th style={thStyle}>Période</th>
                    <th style={thStyle}>Statut</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Jobs</th>
                    <th style={thStyle}>Dernier envoi</th>
                    <th style={thStyle}>Créée par</th>
                    <th style={thStyle} />
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{s.name}</td>
                      <td style={tdStyle}>{fmtDay(s.from)} → {fmtDay(s.to)}</td>
                      <td style={tdStyle}>
                        {s.status === 'OPEN'
                          ? <span style={{ color: 'var(--success)' }}>Ouverte</span>
                          : <span style={{ color: 'var(--text-muted)', display: 'inline-flex', gap: 4, alignItems: 'center' }}><Lock size={12} /> Close</span>}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{s.jobCount}</td>
                      <td style={tdStyle}>{s.lastJobAt ? fmtDateTime(s.lastJobAt) : '—'}</td>
                      <td style={tdStyle}>{s.createdBy ?? '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <button type="button" style={secondaryBtn} onClick={() => open(s.id)} title="Ouvrir la session">
                          <FolderOpen size={13} /> Ouvrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}

      {creating ? (
        <CreateSessionModal
          onClose={() => setCreating(false)}
          onCreated={(s) => { setCreating(false); void loadSessions(); open(s.id); }}
        />
      ) : null}

      <style>{SPIN_CSS}</style>
    </div>
  );
}

function CreateSessionModal({ onClose, onCreated }) {
  const initial = monthPreset(-1);
  const [preset, setPreset] = useState('lastMonth');
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [name, setName] = useState(initial.name);
  const [nameTouched, setNameTouched] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const choose = (key) => {
    setPreset(key);
    if (key === 'custom') return;
    const p = monthPreset(key === 'lastMonth' ? -1 : 0);
    setFrom(p.from);
    setTo(p.to);
    if (!nameTouched) setName(p.name);
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      onCreated(await apiGescomExport.createSession({ name: name.trim(), from, to, notes: notes.trim() || undefined }));
    } catch (e) {
      setError(e.message || 'Création impossible');
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Nouvelle session d’export"
      footer={
        <>
          <button type="button" style={secondaryBtn} onClick={onClose}>Annuler</button>
          <button type="button" style={primaryBtn} onClick={submit} disabled={saving || !name.trim() || !from || !to}>
            {saving ? <Loader size={14} className="spin" /> : <Plus size={14} />} Créer
          </button>
        </>
      }
    >
      <span style={{ display: 'block', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>Période</span>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" style={pill(preset === 'lastMonth')} onClick={() => choose('lastMonth')}>Mois dernier</button>
        <button type="button" style={pill(preset === 'currentMonth')} onClick={() => choose('currentMonth')}>Mois en cours</button>
        <button type="button" style={pill(preset === 'custom')} onClick={() => choose('custom')}>Personnalisée</button>
      </div>
      {preset === 'custom' ? (
        <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5, color: 'var(--text-muted)', minWidth: 160 }}>
            Du <DatePicker value={from} onChange={setFrom} max={to || undefined} style={{ ...inputStyle, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 12.5, color: 'var(--text-muted)', minWidth: 160 }}>
            Au <DatePicker value={to} onChange={setTo} min={from || undefined} style={{ ...inputStyle, marginTop: 4 }} />
          </label>
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--text-muted)' }}>Du {fmtDay(from)} au {fmtDay(to)}</div>
      )}

      <label style={{ display: 'block', fontSize: 13, color: 'var(--text-muted)', margin: '16px 0 6px' }}>Nom</label>
      <input
        value={name}
        onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
        maxLength={120}
        style={inputStyle}
      />
      <label style={{ display: 'block', fontSize: 13, color: 'var(--text-muted)', margin: '12px 0 6px' }}>Notes (optionnel)</label>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 0 }}>La période est figée une fois la session créée.</p>
      {error ? <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 0 }}>{error}</p> : null}
    </Modal>
  );
}
